/**
 * Båt-innlogging på regattasiden — båtnummer + båt-passord (fra båtregisteret).
 *
 * Lagres i localStorage (rc_sail / rc_pw) og deles av alle faner. Med gyldig
 * innlogging meldes båten automatisk på regattaen. Brukes også til tidtaking og
 * sporing. `onLogin` kalles når innloggingen er godkjent, så regattasiden kan
 * åpne deltakerdelen uten omlasting.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.ts';

/** Combobox for båtnummer: åpner liste ved klikk, filtrerer, og tillater fri tekst. */
function BoatCombo({
  value,
  onChange,
  boats,
}: {
  value: string;
  onChange: (v: string) => void;
  boats: { sailNumber: string; boatName: string }[];
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const q = value.trim().toUpperCase();
  const filtered = boats.filter(
    (b) => !q || b.sailNumber.toUpperCase().includes(q) || b.boatName.toUpperCase().includes(q),
  );

  return (
    <div ref={ref} className="relative">
      <input
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder="Velg eller skriv"
        autoCapitalize="characters"
        autoComplete="off"
        className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg font-mono text-sm"
      />
      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-xs">
        ▼
      </span>
      {open && filtered.length > 0 && (
        <ul className="absolute z-30 mt-1 max-h-56 w-full overflow-auto rounded-lg border border-slate-700 bg-slate-900 shadow-lg">
          {filtered.map((b) => (
            <li key={b.sailNumber}>
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  onChange(b.sailNumber);
                  setOpen(false);
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-800"
              >
                <span className="font-mono">{b.sailNumber}</span>
                {b.boatName !== b.sailNumber && (
                  <span className="text-xs text-slate-400 truncate">{b.boatName}</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function RegisterPanel({
  forening,
  onLogin,
}: {
  forening?: string;
  onLogin?: (sail: string, password: string) => void;
}) {
  const boats = useQuery({
    queryKey: ['public-boats', forening],
    queryFn: () => api.publicBoats(forening),
  });
  const [sail, setSail] = useState('');
  const [password, setPassword] = useState('');
  const [debounced, setDebounced] = useState({ sail: '', password: '' });

  useEffect(() => {
    setSail(localStorage.getItem('rc_sail') ?? '');
    setPassword(localStorage.getItem('rc_pw') ?? '');
  }, []);

  // Verifiser litt etter at man har sluttet å skrive (mot båtregisteret).
  useEffect(() => {
    const id = setTimeout(() => setDebounced({ sail: sail.trim().toUpperCase(), password }), 500);
    return () => clearTimeout(id);
  }, [sail, password]);

  const login = useQuery({
    queryKey: ['boat-login', forening, debounced.sail, debounced.password],
    queryFn: () => api.boatLogin(debounced.sail, debounced.password, forening),
    enabled: debounced.sail.length > 0 && debounced.password.length > 0,
    retry: false,
    staleTime: 60_000,
  });

  // Ref, så en ny callback-instans per render ikke fyrer effekten på nytt.
  const onLoginRef = useRef(onLogin);
  onLoginRef.current = onLogin;
  useEffect(() => {
    if (login.isSuccess) onLoginRef.current?.(debounced.sail, debounced.password);
  }, [login.isSuccess, debounced.sail, debounced.password]);

  function saveSail(v: string) {
    const up = v.toUpperCase();
    setSail(up);
    if (up.trim()) localStorage.setItem('rc_sail', up.trim());
    else localStorage.removeItem('rc_sail');
  }
  function savePw(v: string) {
    setPassword(v);
    if (v) localStorage.setItem('rc_pw', v);
    else localStorage.removeItem('rc_pw');
  }
  function reset() {
    setSail('');
    setPassword('');
    localStorage.removeItem('rc_sail');
    localStorage.removeItem('rc_pw');
  }

  const both = sail.trim().length > 0 && password.length > 0;

  return (
    <div className="rounded-2xl bg-slate-900/60 border border-slate-700 p-5 mb-6">
      <h2 className="font-semibold mb-1">🔑 Logg inn med båt for å delta</h2>
      <p className="text-xs text-slate-400 mb-3">
        Startprosedyren ser du uten innlogging. For å delta — fart/kurs, posisjon, sjøkart og
        tidtaking — skriv båtnummer og båt-passord. Båten meldes automatisk på, og innloggingen
        huskes på enheten.
      </p>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-xs text-slate-400 mb-1">Båtnummer</label>
          <BoatCombo value={sail} onChange={saveSail} boats={boats.data ?? []} />
        </div>
        <div>
          <label className="block text-xs text-slate-400 mb-1">Båt-passord</label>
          <input
            type="password"
            value={password}
            onChange={(e) => savePw(e.target.value)}
            placeholder="Passord"
            autoComplete="off"
            className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-sm"
          />
        </div>
      </div>

      <div className="mt-2 flex items-center justify-between gap-3 text-xs">
        {!both ? (
          <span className="text-slate-500">Fyll inn båtnummer og passord.</span>
        ) : login.isLoading ? (
          <span className="text-slate-400">Sjekker…</span>
        ) : login.isSuccess ? (
          <span className="text-emerald-400">
            ✅ Logget inn som {login.data.boatName}{' '}
            <span className="font-mono text-emerald-300">({login.data.sailNumber})</span>
          </span>
        ) : (
          <span className="text-red-300">Feil båtnummer eller båt-passord</span>
        )}
        {(sail || password) && (
          <button onClick={reset} className="text-slate-500 hover:text-slate-300 underline shrink-0">
            Logg ut
          </button>
        )}
      </div>
    </div>
  );
}
